import {
	createElement,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import type { ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
	isDark: boolean;
	toggle: () => void;
}

const STORAGE_KEY = 'a6tr-theme';

function getSystemDark(): boolean {
	return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyDark(dark: boolean) {
	document.documentElement.classList.toggle('dark', dark);
}

const ThemeContext = createContext<ThemeContextValue>({
	isDark: false,
	toggle: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [theme, setThemeState] = useState<Theme>(
		() => (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? 'system',
	);

	const [isDark, setIsDark] = useState<boolean>(() => {
		const stored =
			(localStorage.getItem(STORAGE_KEY) as Theme | null) ?? 'system';
		return stored === 'system' ? getSystemDark() : stored === 'dark';
	});

	useEffect(() => {
		const dark = theme === 'system' ? getSystemDark() : theme === 'dark';
		setIsDark(dark);
		applyDark(dark);
		if (theme === 'system') {
			localStorage.removeItem(STORAGE_KEY);
		} else {
			localStorage.setItem(STORAGE_KEY, theme);
		}
	}, [theme]);

	useEffect(() => {
		if (theme !== 'system') {
			return;
		}
		const mq = window.matchMedia('(prefers-color-scheme: dark)');
		const handler = () => {
			setIsDark(mq.matches);
			applyDark(mq.matches);
		};
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, [theme]);

	const toggle = () => setThemeState(isDark ? 'light' : 'dark');

	return createElement(
		ThemeContext.Provider,
		{ value: { isDark, toggle } },
		children,
	);
}

export function useTheme(): ThemeContextValue {
	return useContext(ThemeContext);
}
